(function (root, factory) {
  'use strict';

  var api = factory(root);

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root && root.document) {
    api.start();
  }
}(typeof window !== 'undefined' ? window : null, function (root) {
  'use strict';

  var document = root && root.document;
  var stateSelector = '[data-biss-cart-backorder-state]';
  var checkoutSelector = 'a[href*="/checkout"], button[name="checkout"], input[name="checkout"], [data-cart-checkout-button]';
  var blockedContainerSelector = '.additional-checkout-buttons';
  var repairPromise = null;
  var repairTimer = null;
  var repairRequested = false;
  var knownMissing = false;
  var resumeCheckoutUntil = 0;
  var started = false;

  function meaningful(value) {
    return typeof value === 'string' && value.trim() !== '';
  }

  function normalizeProperties(properties) {
    if (Array.isArray(properties)) {
      return properties.reduce(function (normalized, entry) {
        if (Array.isArray(entry) && entry.length >= 2 && typeof entry[0] === 'string') {
          normalized[entry[0]] = entry[1];
        }
        return normalized;
      }, {});
    }
    if (!properties || typeof properties !== 'object') {
      return {};
    }
    return Object.assign({}, properties);
  }

  function mergeBackorderProperty(properties, message) {
    var merged = normalizeProperties(properties);
    merged.Backorder = message;
    return merged;
  }

  function clearBackorderProperty(properties) {
    var remaining = normalizeProperties(properties);
    delete remaining.Backorder;
    // Shopify cannot replace an existing properties object with an empty one.
    return Object.keys(remaining).length ? remaining : { Backorder: '' };
  }

  function backorderItemsNeedingRepair(state) {
    var items = state && Array.isArray(state.items) ? state.items : [];

    return items.filter(function (item) {
      var properties = normalizeProperties(item.properties);
      var missingActivePromise = item.purchaseState === 'backorder' &&
        meaningful(item.message) &&
        !meaningful(properties.Backorder);
      var stalePromise = item.backorderOwner === 'boc' &&
        (item.purchaseState === 'in_stock' || item.purchaseState === 'sold_out') &&
        meaningful(properties.Backorder);
      return missingActivePromise || stalePromise;
    });
  }

  function parseState(doc) {
    var node = doc && doc.querySelector(stateSelector);
    if (!node) {
      throw new Error('BISS cart state was not present in the rendered cart.');
    }

    var state = JSON.parse(node.textContent || '{}');
    if (!state || !Array.isArray(state.items)) {
      throw new Error('BISS cart state was malformed.');
    }

    return state;
  }

  function shopRoot() {
    var configured = root && root.Shopify && root.Shopify.routes && root.Shopify.routes.root;
    var value = meaningful(configured) ? configured : '/';
    return value.slice(-1) === '/' ? value : value + '/';
  }

  function cartPath(suffix) {
    return shopRoot() + suffix.replace(/^\//, '');
  }

  function loadRenderedCartState() {
    return root.fetch(cartPath('cart'), {
      credentials: 'same-origin',
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('BISS could not read the current cart.');
      }
      return response.text();
    }).then(function (html) {
      var parsed = new root.DOMParser().parseFromString(html, 'text/html');
      return parseState(parsed);
    });
  }

  function updateLine(item) {
    return root.fetch(cartPath('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties: item.purchaseState === 'backorder'
          ? mergeBackorderProperty(item.properties, item.message)
          : clearBackorderProperty(item.properties)
      })
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('BISS could not update the backorder status on a cart line.');
      }
      return response.json();
    });
  }

  function repairCart(loadState, updateItem, onState, repairedCount) {
    var count = repairedCount || 0;
    return loadState().then(function (state) {
      var pending = backorderItemsNeedingRepair(state);
      if (onState) onState(pending, count);
      if (!pending.length) {
        return { ok: true, changed: count > 0, blocked: false };
      }
      if (count >= 25) {
        throw new Error('BISS stopped an unexpectedly large cart repair.');
      }

      // Re-read after every write because Shopify may merge lines and change keys.
      return updateItem(pending[0]).then(function () {
        return repairCart(loadState, updateItem, onState, count + 1);
      });
    });
  }

  function checkoutControls() {
    return Array.prototype.slice.call(document.querySelectorAll(checkoutSelector));
  }

  function blockedContainers() {
    return Array.prototype.slice.call(document.querySelectorAll(blockedContainerSelector));
  }

  function setCheckoutBlocked(blocked) {
    checkoutControls().forEach(function (control) {
      if (blocked) {
        if (!control.hasAttribute('data-biss-was-disabled')) {
          control.setAttribute('data-biss-was-disabled', control.disabled ? 'true' : 'false');
        }
        if (!control.hasAttribute('data-biss-previous-aria-disabled')) {
          control.setAttribute(
            'data-biss-previous-aria-disabled',
            control.hasAttribute('aria-disabled') ? control.getAttribute('aria-disabled') : '__missing__'
          );
        }
        if ('disabled' in control) control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      } else {
        var wasDisabled = control.getAttribute('data-biss-was-disabled');
        if (wasDisabled !== null) {
          if ('disabled' in control) control.disabled = wasDisabled === 'true';
          control.removeAttribute('data-biss-was-disabled');
        }
        var previousAriaDisabled = control.getAttribute('data-biss-previous-aria-disabled');
        if (previousAriaDisabled !== null) {
          if (previousAriaDisabled === '__missing__') {
            control.removeAttribute('aria-disabled');
          } else {
            control.setAttribute('aria-disabled', previousAriaDisabled);
          }
          control.removeAttribute('data-biss-previous-aria-disabled');
        }
      }
    });

    blockedContainers().forEach(function (container) {
      if (blocked) {
        if (!container.hasAttribute('data-biss-previous-pointer-events')) {
          container.setAttribute('data-biss-previous-pointer-events', container.style.pointerEvents || '');
        }
        if (!container.hasAttribute('data-biss-previous-aria-busy')) {
          container.setAttribute(
            'data-biss-previous-aria-busy',
            container.hasAttribute('aria-busy') ? container.getAttribute('aria-busy') : '__missing__'
          );
        }
        container.style.pointerEvents = 'none';
        container.setAttribute('aria-busy', 'true');
      } else {
        var previous = container.getAttribute('data-biss-previous-pointer-events');
        if (previous !== null) {
          container.style.pointerEvents = previous;
          container.removeAttribute('data-biss-previous-pointer-events');
        }
        var previousAriaBusy = container.getAttribute('data-biss-previous-aria-busy');
        if (previousAriaBusy !== null) {
          if (previousAriaBusy === '__missing__') {
            container.removeAttribute('aria-busy');
          } else {
            container.setAttribute('aria-busy', previousAriaBusy);
          }
          container.removeAttribute('data-biss-previous-aria-busy');
        }
      }
    });
  }

  function statusNode() {
    var existing = document.querySelector('[data-biss-cart-backorder-status]');
    if (existing) return existing;

    var node = document.createElement('p');
    node.hidden = true;
    node.setAttribute('data-biss-cart-backorder-status', '');
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    node.style.color = '#9b1c1c';
    node.style.fontWeight = '600';

    var checkout = document.querySelector(checkoutSelector);
    if (checkout && checkout.parentNode) {
      checkout.parentNode.insertBefore(node, checkout);
    } else {
      document.body.appendChild(node);
    }
    return node;
  }

  function showStatus(message) {
    var node = document.querySelector('[data-biss-cart-backorder-status]');
    if (!message && !node) return;
    node = node || statusNode();
    node.textContent = message || '';
    node.hidden = !message;
  }

  function dispatchComplete(changed) {
    if (typeof root.CustomEvent !== 'function') return;
    document.dispatchEvent(new root.CustomEvent('biss:CartBackorderReconciled', {
      detail: { changed: changed }
    }));
  }

  function runRepair() {
    if (repairPromise) return repairPromise;

    repairPromise = repairCart(loadRenderedCartState, updateLine, function (pending) {
        knownMissing = pending.length > 0;

        if (!pending.length) {
          setCheckoutBlocked(false);
          showStatus('');
          return;
        }

        setCheckoutBlocked(true);
        showStatus('Checking item availability before checkout…');
    }).then(function (result) {
      dispatchComplete(result.changed);
      return result;
    }).catch(function (error) {
      if (knownMissing) {
        setCheckoutBlocked(true);
        showStatus('We could not confirm item availability. Please refresh the cart and try again.');
      } else {
        // Do not turn an unknown/transient read failure into a store-wide outage.
        setCheckoutBlocked(false);
        showStatus('');
      }
      root.console && root.console.error('BISS cart backorder reconciliation failed.', error);
      return { ok: false, changed: false, blocked: knownMissing, error: error };
    }).finally(function () {
      repairPromise = null;
      if (repairRequested) {
        repairRequested = false;
        scheduleRepair(50);
      }
    });

    return repairPromise;
  }

  function scheduleRepair(delay) {
    root.clearTimeout(repairTimer);
    if (repairPromise) {
      repairRequested = true;
      return;
    }
    repairTimer = root.setTimeout(function () {
      runRepair();
    }, typeof delay === 'number' ? delay : 100);
  }

  function checkoutControlFromEvent(event) {
    var target = event && event.target;
    return target && target.closest ? target.closest(checkoutSelector) : null;
  }

  function resumeClick(control) {
    setCheckoutBlocked(false);
    resumeCheckoutUntil = Date.now() + 1500;
    control.click();
  }

  function handleCheckoutClick(event) {
    if (Date.now() < resumeCheckoutUntil) return;

    var control = checkoutControlFromEvent(event);
    if (!control) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    runRepair().then(function (result) {
      if (!result.blocked) resumeClick(control);
    });
  }

  function handleCheckoutSubmit(event) {
    if (Date.now() < resumeCheckoutUntil) return;

    var submitter = event.submitter;
    var isCheckout = submitter && submitter.matches && submitter.matches(checkoutSelector);
    if (!isCheckout) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    runRepair().then(function (result) {
      if (result.blocked) return;
      setCheckoutBlocked(false);
      resumeCheckoutUntil = Date.now() + 1500;
      if (event.target.requestSubmit) {
        event.target.requestSubmit(submitter);
      } else {
        event.target.submit();
      }
    });
  }

  function observeMiniCart() {
    if (typeof root.MutationObserver !== 'function') return;
    var miniCart = document.querySelector('[data-cart-form="mini-cart"]');
    if (!miniCart) return;

    new root.MutationObserver(function () {
      scheduleRepair(150);
    }).observe(miniCart, { childList: true, subtree: true });
  }

  function initialize() {
    var embeddedState;
    try {
      embeddedState = parseState(document);
      knownMissing = backorderItemsNeedingRepair(embeddedState).length > 0;
    } catch (error) {
      knownMissing = false;
    }

    if (knownMissing) setCheckoutBlocked(true);

    root.addEventListener('click', handleCheckoutClick, true);
    root.addEventListener('submit', handleCheckoutSubmit, true);
    document.addEventListener('vitals:CartUpdated', function () { scheduleRepair(150); });
    document.addEventListener('vitals:CartUpdateFinish', function () { scheduleRepair(150); });
    root.addEventListener('pageshow', function () { scheduleRepair(0); });
    observeMiniCart();
    scheduleRepair(0);
  }

  function preblockKnownMissing() {
    try {
      knownMissing = backorderItemsNeedingRepair(parseState(document)).length > 0;
      if (knownMissing) setCheckoutBlocked(true);
    } catch (error) {
      knownMissing = false;
    }
  }

  function start() {
    if (started) return;
    started = true;

    if (document.readyState === 'loading') {
      preblockKnownMissing();
      document.addEventListener('DOMContentLoaded', initialize, { once: true });
    } else {
      initialize();
    }
  }

  return {
    start: start,
    meaningful: meaningful,
    mergeBackorderProperty: mergeBackorderProperty,
    clearBackorderProperty: clearBackorderProperty,
    backorderItemsNeedingRepair: backorderItemsNeedingRepair,
    repairCart: repairCart
  };
}));
